import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import _pick from 'lodash/pick';
import eventsAPI, {
  EventFullDetails,
  EventResponseForm,
} from '../../api/events';
import { getSettings } from '../../api/tenants';
import { Settings } from '../../api/tenants/types/Settings';
import venturesAPI from '../../api/ventures';
import { SnackMessage, Text } from '../../components/common';
import WarningBeforeStart from '../../components/common/Confirmation/WarningBeforeStart';
import ReportFounderSessionNotesForm, {
  FormInitialValues,
  FormValues,
} from '../../components/forms/report-founder-session-notes-form';
import { COLORS } from '../../theme/variables';
import { decodeQuery } from '../../utils/functions';
import { getTenantId } from './utils';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: 0,

    [theme.breakpoints.up('xs')]: {
      width: 'calc(100% - 60px)',
      padding: '50px 30px',
    },

    [theme.breakpoints.up('md')]: {
      width: '100%',
      padding: '50px 0',
    },
  },
  block: {
    width: '700px',
    padding: '32px',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 2px rgba(34, 91, 187, 0.16), 0px 4px 8px rgba(51, 126, 255, 0.04), 0px 8px 16px rgba(51, 126, 255, 0.04)',
    borderRadius: 0,
    boxSizing: 'border-box',

    [theme.breakpoints.up('xs')]: {
      borderRadius: '16px',
    },
  },
  loadingBlock: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingContainer: {
    height: '100vh',
    padding: 0,

    [theme.breakpoints.up('xs')]: {
      width: '100%',
    },
  },
  successBlock: {
    width: 300,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    borderRadius: '16px',
  },
  successIcon: {
    fontSize: 30,
    color: COLORS.COLOR_GREEN_BASE,
    marginBottom: 15,
  },
  errorIcon: {
    fontSize: 30,
    color: COLORS.COLOR_RED_BASE,
    marginBottom: 15,
  },
  successText: {
    textAlign: 'center',
  },
  title: {
    marginBottom: 15,
  },
}));

function parseFilterFromURL(search: string): any {
  return _pick(parseQuery(search), ['key', 'email']);
}

export const formatValuesRequest = (
  values: FormValues,
  fullDetails: EventFullDetails,
): EventResponseForm => {
  return {
    tenantId: fullDetails.event.tenantId,
    eventId: fullDetails.event.id,
    ventureId: fullDetails.event.ventureId,
    sessionRating: +(values.sessionRating || 0),
    ventureProgressRating: 0,
    attendingMentors: [],
    attendingFounders: [],
    mostImpactfulMentor: values?.mostImpactfulMentor || '',
    submitterEmail: values.submitterEmail,
    mentorReport: '',
    mentorReportAttachments: '',
    mentorReportNotes: '',
    founderNotes: values.beforeNextSession || '',
    founderOtherNotes: values.other || '',
    founderNotesAttachments: values.attachments || '',
    agendaQuestion1: '',
    agendaQuestion2: '',
    agendaQuestion3: '',
    agendaQuestion4: '',
    agendaAttachments: '',
    isLeadMentorReport: false,
    nextEventStart: values.nextEventStart || '',
    nextEventEnd: values.nextEventEnd || '',
    isMentorReport: false,
    isFounderReport: true,
    founderReport: false,
    agendaPresented: false,
    ventureReadinessRating: +(values.ventureReadinessRating || 0),
    mentorTeamImpactRating: +(values.mentorTeamImpactRating || 0),
    eventEnjoymentRating: +(values.eventEnjoymentRating || 0),
  };
};

const getInitialValues = (
  fullDetails: EventFullDetails,
  defaultEmail?: string,
): FormInitialValues => {
  return {
    submitterEmail: defaultEmail || '',
    summary: fullDetails?.event.summary || '',
    start: fullDetails?.event.start || '',
    end: fullDetails?.event.end || '',
    sessionRating: 0,
    beforeNextSession: '',
    other: '',
    attachments: [],
    mostImpactfulMentor: '',
    ventureReadinessRating: 0,
    mentorTeamImpactRating: 0,
    eventEnjoymentRating: 0,
    nextEventStart: '',
    nextEventEnd: '',
  };
};

interface Payload extends EventFullDetails {
  formSettings: Partial<Settings>;
}

function ReportPage() {
  const classes = useStyles();
  const location = useLocation();
  const [fullDetails, setFullDetails] = useState<Payload>();
  const [statusForm, setStatusForm] = useState<
    'success' | 'loading' | 'error' | 'no-founders' | ''
  >('loading');
  const [isLoadingSaveForm, setIsLoadingSaveForm] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState('');
  const [errorVaildDate, setErrorVaildDate] = useState(false);

  const { key, email } = useMemo(() => {
    const { key, email } = parseFilterFromURL(location.search);
    return { key, email: email ? (decodeQuery(email) as string) : '' };
  }, [location.search]);

  const initialValues = useMemo(() => {
    if (fullDetails) {
      return getInitialValues(fullDetails, email);
    }
    return undefined;
  }, [email, fullDetails]);

  const optionsMentor = useMemo(() => {
    return fullDetails?.mentorList && fullDetails?.mentorList?.length > 0
      ? fullDetails?.mentorList.map((mentor) => {
          return {
            value: mentor.id,
            label: `${mentor.firstName} ${mentor.lastName}`,
          };
        })
      : [];
  }, [fullDetails?.mentorList]);

  const handleSubmit = async (values: FormValues) => {
    const valuesRequest = formatValuesRequest(
      values,
      fullDetails as EventFullDetails,
    );

    try {
      await eventsAPI.eventsResponseForm(valuesRequest);
      setErrorVaildDate(false);
      setStatusForm('success');
      setIsLoadingSaveForm(false);
    } catch (e: any) {
      setIsLoadingSaveForm(false);
      const messageError = e.response?.data?.message || 'Internal server error';
      enqueueSnackbar('An error occurred while saving your report', {
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  const loadDetails = async (key: string) => {
    const tenantId = getTenantId(key);

    if (!tenantId) {
      setError('Invalid key value');
      setStatusForm('error');
      return;
    }

    try {
      setStatusForm('loading');
      const responseDetails = await eventsAPI.getEventDetails(key);
      const {
        mostImpactfulMentorEnabled,
        founderNotesSessionRatingQuestionEnabled,
        founderNotesVentureReadinessQuestionEnabled,
        founderNotesMentorTeamImpactQuestionEnabled,
        founderNotesSessionEnjoymentQuestionEnabled,
        founderSchedulingEnabled,
      } = await getSettings(tenantId);
      setFullDetails({
        ...responseDetails,
        formSettings: {
          mostImpactfulMentorEnabled,
          founderNotesSessionRatingQuestionEnabled,
          founderNotesVentureReadinessQuestionEnabled,
          founderNotesMentorTeamImpactQuestionEnabled,
          founderNotesSessionEnjoymentQuestionEnabled,
          founderSchedulingEnabled,
        },
      });

      if (!responseDetails.founderList?.length) {
        setStatusForm('no-founders');
      } else {
        setStatusForm('');
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Internal server error');
      setStatusForm('error');
    }
  };

  const handleUploadFile = useCallback(
    async (file: File) => {
      try {
        if (fullDetails?.event.ventureId) {
          const loadedFileURL = await venturesAPI.attachFileToReport(
            fullDetails.event.ventureId,
            fullDetails.event.tenantId,
            file,
          );
          return loadedFileURL;
        }
      } catch (e: any) {
        return '';
      }
    },
    [fullDetails],
  );

  useEffect(() => {
    if (key) {
      loadDetails(key);
    } else {
      setError('Invalid key value');
      setStatusForm('error');
    }
  }, [key]);

  useEffect(() => {
    document.body.style.minWidth = 'auto';
  }, []);

  if (statusForm === 'loading') {
    return (
      <div
        data-testid='report-page'
        className={cn(classes.container, classes.loadingContainer)}>
        <div className={cn(classes.block, classes.loadingBlock)}>
          <CircularProgress size={54} color='primary' />
        </div>
      </div>
    );
  }

  if (statusForm === 'success') {
    return (
      <div
        data-testid='report-page-success'
        className={cn(classes.container, classes.loadingContainer)}>
        <div className={cn(classes.block, classes.successBlock)}>
          <CheckCircleIcon className={classes.successIcon} />
          <div className={classes.successText}>
            <Text variant='normal'>Success!</Text>
          </div>
        </div>
      </div>
    );
  }

  if (statusForm === 'no-founders') {
    return (
      <div
        data-testid='report-page-no-founders'
        className={cn(classes.container, classes.loadingContainer)}>
        <div className={cn(classes.block, classes.successBlock)}>
          <CancelIcon className={classes.errorIcon} />
          <div className={classes.successText}>
            <Text variant='normal'>
              You cannot submit Founder Notes for this session since no Founders
              are assigned.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  if (statusForm === 'error' || !fullDetails) {
    return (
      <div
        data-testid='report-page-error'
        className={cn(classes.container, classes.loadingContainer)}>
        <div className={cn(classes.block, classes.successBlock)}>
          <CancelIcon className={classes.errorIcon} />
          <div className={classes.successText}>
            <Text variant='normal'>
              Unfortunately an error occurred while loading the report. Please
              try again in a couple of minutes. If the problem persists please
              share the URL with the support team at support@tractionfive.com
              <br />
              Details: {error}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <WarningBeforeStart date={fullDetails.event.start} />
      <div data-testid='report-page' className={classes.container}>
        <div className={classes.block}>
          <Typography variant='h2' className={classes.title}>
            Founder Post Session Form
          </Typography>
          <ReportFounderSessionNotesForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            loading={isLoadingSaveForm}
            errorVaildDate={errorVaildDate}
            onUploadFile={handleUploadFile}
            optionsMentor={optionsMentor}
            formSettings={fullDetails.formSettings}
            fullDetails={fullDetails}
          />
        </div>
      </div>
    </>
  );
}

export default ReportPage;
